import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type ThemePreference = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'
type LightVariant = 1 | 2 | 3 | 4 | 5

// Export Theme as the preference type for backwards compatibility
export type Theme = ThemePreference

/**
 * Light Mode Variants:
 * 1 - Warm Cream (Mocha Mousse) - Luxury, wellness, premium AI
 * 2 - Apple Clean (Teal) - Clean tech, professional AI platform
 * 3 - Ocean Blue (Default) - Engineering tools, developer-focused
 * 4 - Sand Dune (Terracotta) - Eco-friendly, organic, natural AI
 * 5 - Soft Lavender (Violet/Rose) - Creative AI, consciousness exploration
 */
export const LIGHT_VARIANT_NAMES: Record<LightVariant, string> = {
  1: 'Warm Cream',
  2: 'Apple Clean',
  3: 'Ocean Blue',
  4: 'Sand Dune',
  5: 'Soft Lavender',
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

interface ThemeContextType {
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
  toggleTheme: () => void
  isDark: boolean
  lightVariant: LightVariant
  setLightVariant: (variant: LightVariant) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'lukhas_theme'
const VARIANT_STORAGE_KEY = 'lukhas_light_variant'

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Theme preference (light/dark/system)
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY) as ThemePreference
      if (saved === 'light' || saved === 'dark' || saved === 'system') {
        return saved
      }
    }
    return 'dark' // LUKHAS defaults to dark mode
  })

  // Resolved theme (actual light/dark based on preference + system)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY) as ThemePreference
      if (saved === 'light' || saved === 'dark') return saved
    }
    return getSystemTheme()
  })

  const [lightVariant, setLightVariantState] = useState<LightVariant>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(VARIANT_STORAGE_KEY)
      if (saved) {
        const num = parseInt(saved, 10) as LightVariant
        if (num >= 1 && num <= 5) return num
      }
    }
    return 2 // Default to Apple Clean (Teal)
  })

  // Apply theme to document
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, themePreference)
    const resolved = themePreference === 'system' ? getSystemTheme() : themePreference
    setResolvedTheme(resolved)
    // Update document class for global styles
    document.documentElement.classList.remove('dark', 'light')
    document.documentElement.classList.add(resolved)
    document.documentElement.style.colorScheme = resolved
  }, [themePreference])

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (themePreference !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light'
      setResolvedTheme(newTheme)
      document.documentElement.classList.remove('light', 'dark')
      document.documentElement.classList.add(newTheme)
      document.documentElement.style.colorScheme = newTheme
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [themePreference])

  useEffect(() => {
    localStorage.setItem(VARIANT_STORAGE_KEY, String(lightVariant))
    // Update data attribute for CSS variant selection
    if (resolvedTheme === 'light') {
      document.documentElement.setAttribute('data-light-variant', String(lightVariant))
    } else {
      document.documentElement.removeAttribute('data-light-variant')
    }
  }, [lightVariant, resolvedTheme])

  const setTheme = (newTheme: ThemePreference) => {
    setThemePreference(newTheme)
  }

  const toggleTheme = () => {
    // Toggle between light and dark (skip system on toggle)
    setThemePreference(prev => {
      if (prev === 'system') {
        return resolvedTheme === 'dark' ? 'light' : 'dark'
      }
      return prev === 'dark' ? 'light' : 'dark'
    })
  }

  const setLightVariant = (variant: LightVariant) => {
    setLightVariantState(variant)
  }

  return (
    <ThemeContext.Provider value={{
      theme: themePreference,
      setTheme,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
      lightVariant,
      setLightVariant
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
